import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { fetchScreenData } from "../../services/api";
import { HomeAudio } from "../../types/ScreenTypes";
import { HomeMasjid, HomeSettings, audioChoiceFor, partnerStartsItself } from "./homeSettings";
import { checkPartnerLive, fetchLiveStatus, isOwnStreamLive, partnerPlayerUrl } from "./liveAudio";

// live.json is a small static file on the stream box (not billed per request), so it's read often:
// the home masjid's broadcast starts within a few seconds of going live.
const POLL_MS = 3_000;
// MyMasjid, and partners when live.json has no listing, go through the backend, which caches its
// answer for a minute anyway: once a minute.
const PARTNER_EVERY_POLLS = 20;
const AUDIO_LINKS_REFRESH_MS = 60 * 60 * 1000;
const FADE_IN_MS = 3000;
const NOT_LIVE_NOTICE_MS = 4000;

export interface LiveAudio {
  liveIds: Set<number>;
  playing: HomeMasjid | null;
  prompt: HomeMasjid | null;
  // startsItself: the app presses play in it (a partner that agreed), so no tap is needed.
  partnerPlayer: { masjid: HomeMasjid; url: string; startsItself: boolean } | null;
  notLive: HomeMasjid | null;
  // YouTube videos live now by masjid id, for any masjid (from the stream box's live.json).
  liveVideo: Record<string, string>;
  // Partner masjids live now by masjid id, for any masjid: eMasjid family and Mixlr.
  livePartners: Record<string, string>;
  video: { masjid: HomeMasjid; videoId: string } | null;
  listen: (masjid: HomeMasjid) => void;
  watch: (masjid: HomeMasjid, videoId: string) => void;
  stop: () => void;
  dismissPrompt: () => void;
  closePartnerPlayer: () => void;
  closeVideo: () => void;
  // The azan is due: close a partner's player or a video (neither can be paused from here).
  makeWayForAzan: () => void;
  findLiveNow: (candidates: HomeMasjid[]) => Promise<HomeMasjid[]>;
}

// Watches the favourites' live audio and plays it as the household chose: automatically (the home
// masjid's own stream, or a partner's that agreed), with a Listen prompt, or not at all. Live video is always a tap
// (about 1.5 GB an hour). One thing plays at a time.
export const useLiveAudio = (settings: HomeSettings | null, offline: boolean): LiveAudio => {
  const [liveIds, setLiveIds] = useState<Set<number>>(new Set());
  const [playing, setPlaying] = useState<HomeMasjid | null>(null);
  const [partnerPlayer, setPartnerPlayer] = useState<LiveAudio["partnerPlayer"]>(null);
  const [notLive, setNotLive] = useState<HomeMasjid | null>(null);
  const [liveVideo, setLiveVideo] = useState<Record<string, string>>({});
  const [livePartners, setLivePartners] = useState<Record<string, string>>({});
  const [video, setVideo] = useState<LiveAudio["video"]>(null);
  // Stopped, "Not now", or set aside for another stream, until that broadcast ends.
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const audioLinks = useRef(new Map<number, HomeAudio | null>());
  const player = useRef<{ masjid: HomeMasjid; audio: HTMLAudioElement; hls?: Hls } | null>(null);
  const dismissedRef = useRef(dismissed);
  dismissedRef.current = dismissed;
  const panelOpenRef = useRef(false);
  panelOpenRef.current = !!partnerPlayer || !!video;
  const wasLive = useRef(new Set<number>());
  const notLiveTimer = useRef(0);

  const dismiss = (masjidId: number) =>
    setDismissed((current) => new Set(current).add(masjidId));

  const loadAudioLinks = async (masjidId: number): Promise<HomeAudio | null> => {
    if (audioLinks.current.has(masjidId)) return audioLinks.current.get(masjidId) ?? null;
    try {
      const links = (await fetchScreenData(masjidId, "home")).homeAudio ?? null;
      audioLinks.current.set(masjidId, links);
      return links;
    } catch {
      return null;
    }
  };

  const stopPlayback = () => {
    const current = player.current;
    player.current = null;
    if (current) {
      current.hls?.destroy();
      current.audio.pause();
      current.audio.removeAttribute("src");
      current.audio.load();
    }
    setPlaying(null);
  };

  // Starting something else sets aside whatever was playing, so its prompt doesn't pop back up.
  const setAsideCurrent = (next: HomeMasjid) => {
    const current = player.current?.masjid;
    if (current && current.id !== next.id) dismiss(current.id);
    stopPlayback();
    setPartnerPlayer(null);
    setVideo(null);
  };

  // Resolves false when the browser refuses to start sound without a tap.
  const play = async (masjid: HomeMasjid, hlsUrl: string): Promise<boolean> => {
    setAsideCurrent(masjid);
    const audio = new Audio();
    let hls: Hls | undefined;
    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(hlsUrl);
      hls.attachMedia(audio);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal && player.current?.audio === audio) stopPlayback();
      });
    } else if (audio.canPlayType("application/vnd.apple.mpegurl")) {
      audio.src = hlsUrl;
    } else {
      return false;
    }
    player.current = { masjid, audio, hls };
    audio.volume = 0;
    try {
      await audio.play();
    } catch {
      if (player.current?.audio === audio) stopPlayback();
      return false;
    }
    // A gentle start, so a broadcast never bursts into a quiet room.
    const started = Date.now();
    const fade = window.setInterval(() => {
      const level = Math.min(1, (Date.now() - started) / FADE_IN_MS);
      audio.volume = level;
      if (level >= 1 || player.current?.audio !== audio) window.clearInterval(fade);
    }, 100);
    setPlaying(masjid);
    return true;
  };

  const openPartnerPlayer = (masjid: HomeMasjid, partner: { provider: string; url: string }) =>
    setPartnerPlayer({
      masjid,
      url: partnerPlayerUrl(partner),
      startsItself: partnerStartsItself(partner.provider),
    });

  const showNotLive = (masjid: HomeMasjid) => {
    setNotLive(masjid);
    window.clearTimeout(notLiveTimer.current);
    notLiveTimer.current = window.setTimeout(() => setNotLive(null), NOT_LIVE_NOTICE_MS);
  };

  // From a tap (prompt, search, Live now), so the browser lets sound start.
  const listen = async (masjid: HomeMasjid) => {
    const links = await loadAudioLinks(masjid.id);
    if (links?.liveStreams.length) {
      const status = await fetchLiveStatus(links.liveStatusUrl);
      const stream = links.liveStreams.find((s) => status?.slugs.has(s.slug));
      if (stream) {
        await play(masjid, stream.hlsUrl);
        return;
      }
    }
    if (links?.partnerAudio) {
      setAsideCurrent(masjid);
      openPartnerPlayer(masjid, links.partnerAudio);
      return;
    }
    showNotLive(masjid);
  };

  const findLiveNow = async (candidates: HomeMasjid[]): Promise<HomeMasjid[]> => {
    const links = await Promise.all(candidates.map((m) => loadAudioLinks(m.id)));
    const statusUrl = links.find((l) => l?.liveStreams.length)?.liveStatusUrl;
    const status = statusUrl ? await fetchLiveStatus(statusUrl) : null;
    return status ? candidates.filter((_, i) => isOwnStreamLive(links[i] ?? undefined, status.slugs)) : [];
  };

  useEffect(() => {
    if (!settings) return;
    let polls = 0;
    let cancelled = false;
    let busy = false;

    const checkLive = async () => {
      const watched = settings.favourites.filter(
        (m) => m.audio && audioChoiceFor(settings, m) !== "off",
      );
      const links = await Promise.all(watched.map((m) => loadAudioLinks(m.id)));
      // live.json is read even with no audio favourites: it also lists live YouTube videos.
      const statusUrl = (links.find((l) => l) ?? (await loadAudioLinks(settings.favourites[0].id)))
        ?.liveStatusUrl;
      const status = statusUrl ? await fetchLiveStatus(statusUrl) : null;
      const slugs = status?.slugs ?? null;
      if (status) {
        setLiveVideo(status.video);
        if (status.partners) setLivePartners(status.partners);
        setVideo((current) => (current && status.video[current.masjid.id] ? current : null));
      }
      const firstCheck = polls === 0;
      const checkPartners = polls++ % PARTNER_EVERY_POLLS === 0;

      const live = new Set<number>();
      await Promise.all(
        watched.map(async (masjid, i) => {
          const link = links[i];
          // A masjid's own stream wins over its partner link; unknown status keeps the last answer.
          // eMasjid-family and Mixlr masjids come from the box's listing in live.json; MyMasjid
          // has none, so it asks the backend.
          const isLive = link?.liveStreams.length
            ? slugs
              ? isOwnStreamLive(link, slugs)
              : wasLive.current.has(masjid.id)
            : link?.partnerAudio
              ? status?.partners && link.partnerAudio.provider !== "mymasjid"
                ? !!status.partners[masjid.id]
                : ((checkPartners ? await checkPartnerLive(link.partnerAudio) : null) ??
                  wasLive.current.has(masjid.id))
              : false;
          if (isLive) live.add(masjid.id);
        }),
      );
      if (cancelled) return;

      // Prompts announce a masjid going live. Favourites already live at start (Masjid Umar
      // broadcasts all day) just show their red dot, except the home masjid.
      if (firstCheck) {
        const alreadyLive = [...live].filter((id) => id !== settings.favourites[0]?.id);
        if (alreadyLive.length) setDismissed((current) => new Set([...current, ...alreadyLive]));
      }

      const ended = [...wasLive.current].filter((id) => !live.has(id));
      if (ended.length) {
        setDismissed((current) => new Set([...current].filter((id) => !ended.includes(id))));
        if (player.current && ended.includes(player.current.masjid.id)) stopPlayback();
        setPartnerPlayer((current) => (current && ended.includes(current.masjid.id) ? null : current));
      }
      for (const [i, masjid] of watched.entries()) {
        const justStarted = live.has(masjid.id) && !wasLive.current.has(masjid.id);
        const link = links[i];
        const stream = link?.liveStreams.find((s) => slugs?.has(s.slug));
        // Only the home masjid starts itself, and only into silence; a refused autoplay (no tap
        // yet in a browser) leaves its Listen prompt showing instead.
        const autoStart =
          justStarted &&
          audioChoiceFor(settings, masjid) === "auto" &&
          !dismissedRef.current.has(masjid.id) &&
          !player.current;
        if (autoStart && stream) {
          await play(masjid, stream.hlsUrl);
        } else if (autoStart && !link?.liveStreams.length && link?.partnerAudio && !panelOpenRef.current) {
          openPartnerPlayer(masjid, link.partnerAudio);
        }
      }
      wasLive.current = live;
      setLiveIds(live);
    };

    const poll = async () => {
      // Starting a stream can take a few seconds; never run two checks over each other.
      if (busy) return;
      busy = true;
      try {
        await checkLive();
      } finally {
        busy = false;
      }
    };

    poll();
    const pollTimer = window.setInterval(poll, POLL_MS);
    const refreshTimer = window.setInterval(() => audioLinks.current.clear(), AUDIO_LINKS_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(pollTimer);
      window.clearInterval(refreshTimer);
    };
    // play/stopPlayback only touch refs and setters, so the poll doesn't restart for them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => () => stopPlayback(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // With no internet nothing is known to be live: drop the dots, prompts and Watch, and close
  // panels that can't play (an open one would also hold back the azan). Polls fill them in again.
  useEffect(() => {
    if (!offline) return;
    wasLive.current = new Set();
    setLiveIds(new Set());
    setLiveVideo({});
    setLivePartners({});
    setVideo(null);
    setPartnerPlayer(null);
  }, [offline]);

  // The first favourite that is live and not already playing, open or set aside: the home masjid first.
  const prompt =
    settings?.favourites.find(
      (m) =>
        liveIds.has(m.id) &&
        audioChoiceFor(settings, m) !== "off" &&
        !dismissed.has(m.id) &&
        playing?.id !== m.id &&
        partnerPlayer?.masjid.id !== m.id,
    ) ?? null;

  return {
    liveIds,
    playing,
    prompt,
    partnerPlayer,
    notLive,
    liveVideo,
    livePartners,
    video,
    listen,
    watch: (masjid, videoId) => {
      setAsideCurrent(masjid);
      setVideo({ masjid, videoId });
    },
    closeVideo: () => setVideo(null),
    makeWayForAzan: () => {
      setPartnerPlayer(null);
      setVideo(null);
    },
    stop: () => {
      if (player.current) dismiss(player.current.masjid.id);
      stopPlayback();
    },
    dismissPrompt: () => prompt && dismiss(prompt.id),
    closePartnerPlayer: () => {
      // "Not now" lasts until the broadcast ends, so it only counts while one is on: closing the
      // player to check an offline masjid mustn't silence its next broadcast.
      if (partnerPlayer && liveIds.has(partnerPlayer.masjid.id)) dismiss(partnerPlayer.masjid.id);
      setPartnerPlayer(null);
    },
    findLiveNow,
  };
};
